import { I18nProvider } from '@lingui/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { i18n } from './i18n'
import './index.css'
import {
  getProperties,
  initializeFaviconAndTitle,
  initializeI18n
} from './core/utils/init-app'
import { PrivateApp } from './PrivateApp'
import { PublicApp } from './PublicApp'
import { publicRoutes } from './core/router/routes'
import * as serviceWorker from './serviceWorker'
import { hideGlobalLoading } from './core/components/global-loading'
import { ServerOfflineScreen } from './core/components/server-offline-screen'
import { initKeycloak } from './core/utils/keykloack'
import { initAxios } from './core/utils/axios'
import { initApollo } from './core/utils/apollo'
import { initAnalytics } from './core/utils/analytics'
import { ApolloProvider } from '@apollo/client'
import { SnackbarProvider } from '@tecsinapse/cortex-react'
import { BrowserRouter } from 'react-router-dom'
import { logout } from './core/utils/logout-keycloack'
import { KeycloakContext } from './core/utils/keycloak-context'

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')
const root = createRoot(rootElement)
const isDev = import.meta.env.VITE_PROFILE === 'development'

;(async function initApp() {
  try {
    const {
      properties,
      keycloak: keycloakConfig,
      manifest
    } = await getProperties()

    const kcInstance = await initKeycloak(keycloakConfig, publicRoutes)

    initAxios(`${import.meta.env.VITE_API_URL}`, kcInstance, publicRoutes)

    const apolloClient = initApollo(
      `${import.meta.env.VITE_API_URL}/api/graphql`,
      kcInstance,
      publicRoutes
    )

    await initializeI18n(apolloClient)
    initializeFaviconAndTitle(manifest)
    initAnalytics(!isDev ? properties.portalAnalyticsCode : undefined)

    if (kcInstance.authenticated) {
      serviceWorker.register(manifest.vapid_public_key, apolloClient)
    }
    hideGlobalLoading()

    root.render(
      <>
        <ApolloProvider client={apolloClient}>
          <KeycloakContext.Provider
            value={{
              keycloak: kcInstance,
              logout: () => logout(kcInstance, apolloClient)
            }}
          >
            <SnackbarProvider>
              <BrowserRouter>
                <I18nProvider i18n={i18n}>
                  {kcInstance.authenticated ? (
                    <PrivateApp manifest={manifest} />
                  ) : (
                    <PublicApp manifest={manifest} />
                  )}
                </I18nProvider>
              </BrowserRouter>
            </SnackbarProvider>
          </KeycloakContext.Provider>
        </ApolloProvider>
      </>
    )
  } catch (e) {
    console.error(e)
    hideGlobalLoading()
    root.render(<ServerOfflineScreen />)
  }
})()
